import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useInView } from 'react-intersection-observer';
import { useNavigate } from 'react-router-dom';
import { useStrings } from '../hooks/useConfig';
import { configService } from '../services/configService';
import {
  Grid,
  Box,
  SpaceBetween,
  Spinner,
  Alert,
  Modal,
  Button
} from '@cloudscape-design/components';
import { PhotoWithUrls } from '../types';
import { boxApiService } from '../services/boxApi';

interface PhotoModalProps {
  photo: PhotoWithUrls;
  onClose: () => void;
}

const PhotoModal: React.FC<PhotoModalProps> = ({ photo, onClose }) => {
  const { strings } = useStrings();
  const [fullImageUrl, setFullImageUrl] = useState<string | null>(null);
  const [imageLoading, setImageLoading] = useState(true);
  const [imageError, setImageError] = useState(false);
  const [downloading, setDownloading] = useState(false);

  useEffect(() => {
    const loadFullImage = async () => {
      setImageLoading(true);
      setImageError(false);

      try {
        // Try to get the download URL for full resolution
        let imageUrl = photo.downloadUrl;

        if (!imageUrl) {
          // Fallback to getting download URL from API
          imageUrl = await boxApiService.getFileDownloadUrl(photo.id);
        }

        // Check if the URL is accessible (not a placeholder)
        if (imageUrl && !imageUrl.startsWith('#file-')) {
          setFullImageUrl(imageUrl);
        } else {
          // Use thumbnail as fallback
          setFullImageUrl(photo.thumbnailUrl || null);
        }
      } catch (error) {
        console.error('Failed to load full image:', error);
        setImageError(true);
        // Use thumbnail as fallback
        setFullImageUrl(photo.thumbnailUrl || null);
      } finally {
        setImageLoading(false);
      }
    };

    loadFullImage();
  }, [photo.id, photo.downloadUrl, photo.thumbnailUrl]);

  const handleDownload = async () => {
    setDownloading(true);

    try {
      let downloadUrl = photo.downloadUrl;

      // If download URL is a lazy placeholder, fetch it now
      if (downloadUrl.startsWith('#file-') && downloadUrl.includes('-lazy')) {
        console.log('Fetching download URL on-demand for:', photo.id);
        try {
          const response = await boxApiService.getFileDownloadUrl(photo.id);
          downloadUrl = response;
        } catch (error) {
          console.error('Failed to get download URL:', error);
          alert(strings?.modal.downloadError || 'Download not available for this image');
          return;
        }
      }

      if (!downloadUrl || downloadUrl.startsWith('#file-')) {
        alert(strings?.modal.downloadError || 'Download not available for this image');
        return;
      }

      // Create a temporary link to trigger download
      const link = document.createElement('a');
      link.href = downloadUrl;
      link.download = photo.name;
      link.target = '_blank';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch (error) {
      console.error('Download failed:', error);
      alert(strings?.modal.downloadFailed || 'Failed to download image');
    } finally {
      setDownloading(false);
    }
  };

  return (
    <Modal
      onDismiss={onClose}
      visible={true}
      size="max"
      header={photo.name}
      footer={
        <Box float="right">
          <SpaceBetween direction="horizontal" size="xs">
            <Button
              onClick={handleDownload}
              loading={downloading}
              disabled={!fullImageUrl || fullImageUrl.startsWith('#file-')}
              iconName="download"
            >
              {strings?.modal.download || 'Download'}
            </Button>
            <Button onClick={onClose}>{strings?.modal.close || 'Close'}</Button>
          </SpaceBetween>
        </Box>
      }
    >
      <div style={{ textAlign: 'center', minHeight: '400px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        {imageLoading ? (
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '16px' }}>
            <Spinner size="large" />
            <div>{strings?.modal.loading || 'Loading full resolution image...'}</div>
          </div>
        ) : imageError ? (
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '16px', color: '#666' }}>
            <div>{strings?.modal.loadError || 'Failed to load full resolution image'}</div>
            {photo.thumbnailUrl && (
              <img
                src={photo.thumbnailUrl}
                alt={photo.name}
                style={{
                  maxWidth: '100%',
                  maxHeight: '70vh',
                  objectFit: 'contain'
                }}
              />
            )}
          </div>
        ) : fullImageUrl ? (
          <img
            src={fullImageUrl}
            alt={photo.name}
            style={{
              maxWidth: '100%',
              maxHeight: '80vh',
              objectFit: 'contain'
            }}
            onError={() => {
              console.error('Full image failed to load, showing thumbnail');
              setImageError(true);
            }}
          />
        ) : (
          <div>{strings?.modal.noImage || 'No image available'}</div>
        )}
      </div>
    </Modal>
  );
};

interface PhotoGridProps {
  folderId?: string;
  fileId?: string;
}

// PhotoWithUrls type is now imported from types

export const PhotoGrid: React.FC<PhotoGridProps> = ({ folderId = '0', fileId }) => {
  const navigate = useNavigate();
  const { strings } = useStrings();
  const [photos, setPhotos] = useState<PhotoWithUrls[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [hasMore, setHasMore] = useState(true);
  const [selectedPhoto, setSelectedPhoto] = useState<PhotoWithUrls | null>(null);
  const [photoUrls, setPhotoUrls] = useState<Record<string, string>>({});
  const [criticalError, setCriticalError] = useState(false);
  const [processingThumbnails, setProcessingThumbnails] = useState<Set<string>>(new Set());
  const [retryingThumbnails, setRetryingThumbnails] = useState<Set<string>>(new Set());
  const [searchingForFileId, setSearchingForFileId] = useState<string | null>(null);
  const [failedThumbnails, setFailedThumbnails] = useState<Set<string>>(new Set());
  const [showFailureBanner, setShowFailureBanner] = useState(false);
  const bannerTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const loadingRef = useRef(false);
  const retryTimeouts = useRef<Record<string, NodeJS.Timeout>>({});

  const { ref, inView } = useInView({
    threshold: 0,
    rootMargin: '100px',
  });

  const retryThumbnail: (photoId: string) => Promise<void> = useCallback(async (photoId: string) => {
    try {
      console.log(`Retrying thumbnail for photo: ${photoId}`);
      const thumbnailInfo = await boxApiService.getFileThumbnail(photoId);

      if (thumbnailInfo.status === 'ready') {
        // Thumbnail is now ready
        setPhotoUrls(prev => ({ ...prev, [photoId]: thumbnailInfo.url }));
        setProcessingThumbnails(prev => {
          const newSet = new Set(prev);
          newSet.delete(photoId);
          return newSet;
        });
        setRetryingThumbnails(prev => {
          const newSet = new Set(prev);
          newSet.delete(photoId);
          return newSet;
        });
        console.log(`Thumbnail ready for photo: ${photoId}`);
      } else if (thumbnailInfo.status === 'processing' && thumbnailInfo.retryAfter) {
        // Still processing, schedule another retry
        setRetryingThumbnails(prev => {
          const newSet = new Set(prev);
          newSet.delete(photoId);
          return newSet;
        });
        setProcessingThumbnails(prev => new Set(prev).add(photoId));
        retryTimeouts.current[photoId] = setTimeout(() => {
          retryThumbnail(photoId);
        }, thumbnailInfo.retryAfter * 1000);
        console.log(`Thumbnail still processing for photo: ${photoId}, retry in ${thumbnailInfo.retryAfter}s`);

        // Only retry once - check if we've already retried this photo
        if (!retryingThumbnails.has(photoId)) {
          setRetryingThumbnails(prev => new Set(prev).add(photoId));
          retryTimeouts.current[photoId] = setTimeout(() => {
            retryThumbnail(photoId);
          }, thumbnailInfo.retryAfter * 1000);
        } else {
          // Already retried once, stop retrying
          setProcessingThumbnails(prev => {
            const newSet = new Set(prev);
            newSet.delete(photoId);
            return newSet;
          });
          setRetryingThumbnails(prev => {
            const newSet = new Set(prev);
            newSet.delete(photoId);
            return newSet;
          });
          console.log(`Thumbnail retry limit reached for photo: ${photoId}`);
        }
      } else if (thumbnailInfo.status === 'unavailable') {
        // Thumbnail is unavailable, stop retrying
        setPhotoUrls(prev => ({ ...prev, [photoId]: thumbnailInfo.url }));
        setProcessingThumbnails(prev => {
          const newSet = new Set(prev);
          newSet.delete(photoId);
          return newSet;
        });
        setRetryingThumbnails(prev => {
          const newSet = new Set(prev);
          newSet.delete(photoId);
          return newSet;
        });
        console.log(`Thumbnail permanently unavailable for photo: ${photoId}`);
      } else {
        // Other error states
        setPhotoUrls(prev => ({ ...prev, [photoId]: thumbnailInfo.url }));
        setProcessingThumbnails(prev => {
          const newSet = new Set(prev);
          newSet.delete(photoId);
          return newSet;
        });
        setRetryingThumbnails(prev => {
          const newSet = new Set(prev);
          newSet.delete(photoId);
          return newSet;
        });
        console.log(`Thumbnail error for photo: ${photoId}`);
      }
    } catch (error) {
      console.error(`Failed to retry thumbnail for photo: ${photoId}`, error);
      // Remove from both sets on error
      setProcessingThumbnails(prev => {
        const newSet = new Set(prev);
        newSet.delete(photoId);
        return newSet;
      });
      setRetryingThumbnails(prev => {
        const newSet = new Set(prev);
        newSet.delete(photoId);
        return newSet;
      });
    }
  }, [boxApiService]);

  const loadPhotos = useCallback(async (reset: boolean = false) => {
    if (loadingRef.current || criticalError) return;

    loadingRef.current = true;
    setLoading(true);
    setError(null);

    try {
      // Calculate offset based on current photos length for append, 0 for reset
      const currentOffset = reset ? 0 : photos.length;
      console.log(`Loading photos: reset=${reset}, currentOffset=${currentOffset}, folderId=${folderId}`);

      const newPhotos = await boxApiService.getImageFiles(folderId, 20, currentOffset);
      console.log(`Received ${newPhotos.length} new photos`);

      if (reset) {
        setPhotos(newPhotos);
        // Reset successful
      } else {
        setPhotos(prev => {
          const combined = [...prev, ...newPhotos];
          console.log(`Total photos after append: ${combined.length}`);
          return combined;
        });
      }

      // Photos loaded successfully

      // Set hasMore based on whether we got a full batch
      setHasMore(newPhotos.length === 20);
      console.log(`Has more photos: ${newPhotos.length === 20}`);

      // Photos now come with thumbnailUrl and downloadUrl from the backend
      const urls: Record<string, string> = {};
      const processing = new Set<string>();

      const retrying = new Set<string>();

      newPhotos.forEach(photo => {
        urls[photo.id] = photo.thumbnailUrl;

        // Handle processing thumbnails
        if (photo.thumbnailStatus === 'processing' && photo.thumbnailRetryAfter) {
          processing.add(photo.id);
          // Schedule retry after the specified time
          retryTimeouts.current[photo.id] = setTimeout(() => {
            retryThumbnail(photo.id);
          }, photo.thumbnailRetryAfter * 1000);
        } else if (photo.thumbnailStatus === 'unavailable') {
          // Don't auto-retry unavailable thumbnails to save API calls
          // Let browser handle the direct URL - if it fails, it fails
          console.log(`Thumbnail marked as unavailable for: ${photo.id}, no auto-retry`);
        }
      });

      setPhotoUrls(prev => ({ ...prev, ...urls }));
      setProcessingThumbnails(prev => new Set([...Array.from(prev), ...Array.from(processing)]));
      setRetryingThumbnails(prev => new Set([...Array.from(prev), ...Array.from(retrying)]));
    } catch (err: any) {
      console.error('Error loading photos:', err);

      // Handle critical errors that should stop loading
      if (err.response?.status === 401 ||
        err.response?.status === 403 ||
        err.message?.includes('authentication') ||
        err.message?.includes('JWT')) {
        setCriticalError(true);
        setError(strings?.errors.failedToLoadConfig || 'Failed to load photos. Please check the backend configuration.');
        setHasMore(false);
      } else {
        setError(strings?.errors.failedToLoad || 'Failed to load photos. Please try again.');
      }
    } finally {
      loadingRef.current = false;
      setLoading(false);
    }
  }, [folderId, criticalError, photos.length, strings?.errors.failedToLoad, strings?.errors.failedToLoadConfig]);

  // Reset and load initial photos when folder changes
  useEffect(() => {
    console.log(`Folder changed to: ${folderId}, resetting photos`);

    // Clear any pending retry timeouts
    Object.values(retryTimeouts.current).forEach(timeout => clearTimeout(timeout));
    retryTimeouts.current = {};

    // Clear banner timeout
    if (bannerTimeoutRef.current) {
      clearTimeout(bannerTimeoutRef.current);
      bannerTimeoutRef.current = null;
    }

    setPhotos([]);
    setHasMore(true);
    setCriticalError(false);
    setError(null);
    setPhotoUrls({});
    setProcessingThumbnails(new Set());
    setRetryingThumbnails(new Set());
    setSearchingForFileId(null);
    setFailedThumbnails(new Set());
    setShowFailureBanner(false);

    // Load initial photos
    const loadInitial = async () => {
      if (loadingRef.current) return;

      loadingRef.current = true;
      setLoading(true);

      try {
        const newPhotos = await boxApiService.getImageFiles(folderId, 20, 0);
        console.log(`Initial load: received ${newPhotos.length} photos`);

        setPhotos(newPhotos);
        setHasMore(newPhotos.length === 20);

        const urls: Record<string, string> = {};
        const processing = new Set<string>();
        const retrying = new Set<string>();

        newPhotos.forEach(photo => {
          urls[photo.id] = photo.thumbnailUrl;

          // Handle processing thumbnails
          if (photo.thumbnailStatus === 'processing' && photo.thumbnailRetryAfter) {
            processing.add(photo.id);
            // Schedule retry after the specified time
            retryTimeouts.current[photo.id] = setTimeout(() => {
              retryThumbnail(photo.id);
            }, photo.thumbnailRetryAfter * 1000);
          } else if (photo.thumbnailStatus === 'unavailable') {
            // Don't auto-retry unavailable thumbnails to save API calls
            console.log(`Thumbnail marked as unavailable for: ${photo.id}, no auto-retry`);
          }
        });

        setPhotoUrls(urls);
        setProcessingThumbnails(processing);
        setRetryingThumbnails(retrying);

      } catch (err: any) {
        console.error('Error loading initial photos:', err);
        setError(strings?.errors.failedToLoad || 'Failed to load photos');
        setCriticalError(true);
      } finally {
        loadingRef.current = false;
        setLoading(false);
      }
    };

    loadInitial();
  }, [folderId, strings?.errors.failedToLoad]);

  // Cleanup timeouts on unmount
  useEffect(() => {
    return () => {
      Object.values(retryTimeouts.current).forEach(timeout => clearTimeout(timeout));
      if (bannerTimeoutRef.current) {
        clearTimeout(bannerTimeoutRef.current);
      }
    };
  }, []);

  // Handle fileId in URL - open modal if fileId is provided
  useEffect(() => {
    if (!fileId) {
      // Close modal if no fileId in URL
      setSelectedPhoto(null);
      setSearchingForFileId(null);
      return;
    }

    // First check if photo is already loaded
    const existingPhoto = photos.find(p => p.id === fileId);
    if (existingPhoto) {
      setSelectedPhoto(existingPhoto);
      setSearchingForFileId(null);
      return;
    }

    // If not found in loaded photos, fetch it directly
    if (searchingForFileId !== fileId) {
      setSearchingForFileId(fileId);

      const fetchSpecificPhoto = async () => {
        try {
          console.log(`Fetching specific photo: ${fileId}`);
          const photo = await boxApiService.getFileInfo(fileId);

          if (photo) {
            // Photo exists and is an image, open modal
            setSelectedPhoto(photo);
            setSearchingForFileId(null);

            // Add to photos list if not already there
            setPhotos(prev => {
              const exists = prev.find(p => p.id === fileId);
              if (!exists) {
                return [photo, ...prev];
              }
              return prev;
            });
          } else {
            // Photo doesn't exist or is not an image, redirect to folder
            console.warn(`Photo with ID ${fileId} not found or is not an image`);
            setSearchingForFileId(null);
            navigate(`/${folderId}`);
          }
        } catch (error) {
          console.error(`Failed to fetch photo ${fileId}:`, error);
          setSearchingForFileId(null);
          navigate(`/${folderId}`);
        }
      };

      fetchSpecificPhoto();
    }
  }, [fileId, photos, folderId, navigate, searchingForFileId]);

  // Handle infinite scroll
  useEffect(() => {
    console.log(`Infinite scroll check: inView=${inView}, hasMore=${hasMore}, loading=${loading}, criticalError=${criticalError}`);

    if (inView && hasMore && !loading && !criticalError && !loadingRef.current) {
      console.log('Triggering infinite scroll load');
      loadPhotos(false);
    }
  }, [inView, hasMore, loading, criticalError, loadPhotos]);

  const openModal = (photo: PhotoWithUrls) => {
    setSelectedPhoto(photo);
    // Update URL to include fileId
    navigate(`/${folderId}/${photo.id}`);
  };

  const closeModal = () => {
    setSelectedPhoto(null);
    // Remove fileId from URL
    navigate(`/${folderId}`);
  };

  const handleRetry = () => {
    setCriticalError(false);
    setError(null);
    setPhotos([]);
    setHasMore(true);
    // Trigger reload by calling the initial load logic
    window.location.reload();
  };

  if (error && photos.length === 0) {
    return (
      <Alert
        type="error"
        header={strings?.errors.loadPhotos || 'Error loading photos'}
        action={
          criticalError ? (
            <Button onClick={handleRetry}>
              {strings?.errors.retry || 'Retry'}
            </Button>
          ) : undefined
        }
      >
        {error}
        {criticalError && (
          <div style={{ marginTop: '10px' }}>
            <p>{strings?.errors.reasons.title || 'This might be due to:'}</p>
            <ul>
              <li>{strings?.errors.reasons.auth || 'Backend authentication configuration issues'}</li>
              <li>{strings?.errors.reasons.credentials || 'Box API credentials problems'}</li>
              <li>{strings?.errors.reasons.network || 'Network connectivity issues'}</li>
            </ul>
          </div>
        )}
      </Alert>
    );
  }

  return (
    <SpaceBetween direction="vertical" size="l">
      {showFailureBanner && failedThumbnails.size > 0 && (
        <Alert
          type="info"
          dismissible
          onDismiss={() => setShowFailureBanner(false)}
          header="Image Processing Notice"
          action={
            <Button
              onClick={() => window.location.reload()}
              iconName="refresh"
            >
              {strings?.banner.refresh || 'Refresh Page'}
            </Button>
          }
        >
          {strings?.banner.thumbnailFailure || 'Some images are still being processed by Box. Please try refreshing the page in 5 minutes.'}
          <br />
          <small>Affected images: {failedThumbnails.size}</small>
        </Alert>
      )}
      {searchingForFileId && (
        <div style={{ textAlign: 'center', padding: '20px' }}>
          <SpaceBetween direction="horizontal" size="xs" alignItems="center">
            <Spinner />
            <span>{strings?.grid.searchingPhoto || 'Loading photo...'}</span>
          </SpaceBetween>
        </div>
      )}
      <Grid
        gridDefinition={[
          { colspan: { default: 12, xs: 6, s: 4, m: 3, l: 2, xl: 2 } },
          { colspan: { default: 12, xs: 6, s: 4, m: 3, l: 2, xl: 2 } },
          { colspan: { default: 12, xs: 6, s: 4, m: 3, l: 2, xl: 2 } },
          { colspan: { default: 12, xs: 6, s: 4, m: 3, l: 2, xl: 2 } },
          { colspan: { default: 12, xs: 6, s: 4, m: 3, l: 2, xl: 2 } },
          { colspan: { default: 12, xs: 6, s: 4, m: 3, l: 2, xl: 2 } },
        ]}
      >
        {photos.map((photo) => (
          <Box key={photo.id} padding="xs">
            <div
              style={{
                aspectRatio: '1',
                overflow: 'hidden',
                borderRadius: '8px',
                cursor: 'pointer',
                backgroundColor: '#f5f5f5',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                minHeight: '200px'
              }}
              onClick={() => openModal(photo)}
            >
              {photoUrls[photo.id] ? (
                <div style={{
                  position: 'relative',
                  width: '100%',
                  // height: '100%' 
                }}>
                  <img
                    src={photoUrls[photo.id]}
                    alt={photo.name}
                    style={{
                      width: '100%',
                      // height: '100%',
                      objectFit: 'cover',
                      display: 'block'
                    }}
                    onError={(e) => {
                      console.error(`Failed to load image for ${photo.name}:`, photoUrls[photo.id]);
                      // Track failed thumbnail
                      setFailedThumbnails(prev => new Set(prev).add(photo.id));
                      // Show banner after a short delay to avoid showing for temporary issues
                      if (bannerTimeoutRef.current) {
                        clearTimeout(bannerTimeoutRef.current);
                      }
                      bannerTimeoutRef.current = setTimeout(() => {
                        setShowFailureBanner(true);
                      }, 2000); // Show banner after 2 seconds of failures
                      // Show placeholder on error (could be 202/302 from Box)
                      e.currentTarget.src = `${process.env.PUBLIC_URL || ''}/placeholders/image-error.svg`;
                    }}
                    onLoad={() => {
                      console.log(`Successfully loaded image for ${photo.name}`);
                      // Remove from all tracking sets if successful
                      setProcessingThumbnails(prev => {
                        const newSet = new Set(prev);
                        newSet.delete(photo.id);
                        return newSet;
                      });
                      setRetryingThumbnails(prev => {
                        const newSet = new Set(prev);
                        newSet.delete(photo.id);
                        return newSet;
                      });
                      setFailedThumbnails(prev => {
                        const newSet = new Set(prev);
                        newSet.delete(photo.id);
                        // Hide banner if no more failed thumbnails
                        if (newSet.size === 0) {
                          setShowFailureBanner(false);
                          // Clear any pending banner timeout
                          if (bannerTimeoutRef.current) {
                            clearTimeout(bannerTimeoutRef.current);
                            bannerTimeoutRef.current = null;
                          }
                        }
                        return newSet;
                      });
                    }}
                  />
                  {(processingThumbnails.has(photo.id) || retryingThumbnails.has(photo.id)) && (
                    <div
                      style={{
                        position: 'absolute',
                        top: '8px',
                        right: '8px',
                        backgroundColor: 'rgba(0, 0, 0, 0.7)',
                        color: 'white',
                        padding: '4px 8px',
                        borderRadius: '4px',
                        fontSize: '10px',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '4px'
                      }}
                    >
                      <Spinner />
                      {retryingThumbnails.has(photo.id)
                        ? (strings?.grid.retrying || 'Retrying...')
                        : (strings?.grid.processing || 'Processing...')
                      }
                    </div>
                  )}
                  {failedThumbnails.has(photo.id) && (
                    <div
                      style={{
                        position: 'absolute',
                        top: '8px',
                        left: '8px',
                        backgroundColor: 'rgba(255, 68, 68, 0.9)',
                        color: 'white',
                        padding: '4px 8px',
                        borderRadius: '4px',
                        fontSize: '10px',
                        cursor: 'pointer'
                      }}
                      onClick={(e) => {
                        e.stopPropagation();
                        // Retry loading the image
                        const img = e.currentTarget.parentElement?.querySelector('img');
                        if (img) {
                          // Remove from failed set and try reloading
                          setFailedThumbnails(prev => {
                            const newSet = new Set(prev);
                            newSet.delete(photo.id);
                            return newSet;
                          });
                          // Force reload by changing src
                          const originalSrc = photoUrls[photo.id];
                          img.src = originalSrc + (originalSrc.includes('?') ? '&' : '?') + 'retry=' + Date.now();
                        }
                      }}
                    >
                      ↻ Retry
                    </div>
                  )}
                </div>
              ) : (
                <div
                  style={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    color: '#666',
                    textAlign: 'center'
                  }}
                >
                  <Spinner />
                  <div style={{ marginTop: '8px', fontSize: '12px' }}>
                    {strings?.grid.loading || 'Loading...'}
                  </div>
                </div>
              )}
            </div>
          </Box>
        ))}
      </Grid>

      {hasMore && (
        <div ref={ref} style={{ textAlign: 'center', padding: '20px' }}>
          {loading && <Spinner size="large" />}
        </div>
      )}

      {selectedPhoto && (
        <PhotoModal
          photo={selectedPhoto}
          onClose={closeModal}
        />
      )}
    </SpaceBetween>
  );
};

// Download All Button Component
interface DownloadAllButtonProps {
  folderId: string;
}

const DownloadAllButton: React.FC<DownloadAllButtonProps> = ({ folderId }) => {
  const { strings } = useStrings();
  const [folderInfo, setFolderInfo] = useState<any>(null);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    const loadFolderInfo = async () => {
      try {
        setIsLoading(true);
        const info = await configService.getFolderInfo(folderId);
        setFolderInfo(info);
      } catch (error) {
        console.error('Failed to load folder info:', error);
      } finally {
        setIsLoading(false);
      }
    };

    loadFolderInfo();
  }, [folderId]);

  const handleDownloadZip = () => {
    if (!folderInfo?.zipDownloadUrl) {
      alert('Download not available for this folder');
      return;
    }

    // Create download link for the pre-generated zip
    const link = document.createElement('a');
    link.href = folderInfo.zipDownloadUrl;
    link.download = `${folderInfo.displayName}.zip`;
    link.style.display = 'none';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);

    setShowConfirmModal(false);
  };

  const handleButtonClick = () => {
    if (!folderInfo?.zipDownloadUrl) {
      alert('Download not available for this folder');
      return;
    }
    setShowConfirmModal(true);
  };

  // Don't show button if no zip download available
  if (isLoading || !folderInfo?.zipDownloadUrl) {
    return null;
  }

  return (
    <>
      <Button
        onClick={handleButtonClick}
        iconName="download"
      >
        {strings?.downloadAll.button || 'Download All'}
      </Button>

      <Modal
        visible={showConfirmModal}
        onDismiss={() => setShowConfirmModal(false)}
        header={strings?.downloadAll.confirmTitle || 'Download All Images'}
        footer={
          <Box float="right">
            <SpaceBetween direction="horizontal" size="xs">
              <Button onClick={() => setShowConfirmModal(false)}>
                {strings?.downloadAll.cancel || 'Cancel'}
              </Button>
              <Button 
                variant="primary" 
                onClick={handleDownloadZip}
              >
                {strings?.downloadAll.confirm || 'Download'}
              </Button>
            </SpaceBetween>
          </Box>
        }
      >
        <SpaceBetween direction="vertical" size="m">
          <div>
            <strong>{folderInfo.displayName}</strong>
          </div>
          <div>
            This will download a pre-generated zip file containing all images from this folder.
          </div>
          {folderInfo.zipFileSize && (
            <div>
              <small>File size: {folderInfo.zipFileSize}</small>
            </div>
          )}
          {folderInfo.zipLastUpdated && (
            <div>
              <small>Last updated: {folderInfo.zipLastUpdated}</small>
            </div>
          )}
        </SpaceBetween>
      </Modal>
    </>
  );
};

// Export the DownloadAllButton component
export { DownloadAllButton };